import type { RichText } from "./blocks";

/**
 * The editor's way of writing rich text (ADR 0033). Staff type into a plain box with a few familiar shortcuts, and
 * this turns what they typed into the structured nodes a block stores, and back again. Nothing here produces HTML.
 *
 *   ## Heading          ### Smaller heading
 *   - item              1. numbered item
 *   > quote             — who said it (last line of a quote)
 *   **bold**   *italic*   [link text](/address)
 *
 * Blank lines separate paragraphs, headings, lists and quotes.
 */

type Node = RichText[number];
type Inline = Extract<Node, { type: "paragraph" }>["content"][number];

const INLINE = /\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

/** A line of text into runs: plain, bold, italic, or linked. */
export function inlinesFromMarkup(line: string): Inline[] {
  const runs: Inline[] = [];
  let last = 0;
  for (const match of line.matchAll(INLINE)) {
    const at = match.index;
    if (at > last) runs.push({ text: line.slice(last, at) });
    const [, bold, italic, label, href] = match;
    if (bold !== undefined) runs.push({ text: bold, bold: true });
    else if (italic !== undefined) runs.push({ text: italic, italic: true });
    else if (label !== undefined && href !== undefined) runs.push({ text: label, href });
    last = at + match[0].length;
  }
  if (last < line.length) runs.push({ text: line.slice(last) });
  return runs.filter((run) => run.text.length > 0);
}

export function inlinesToMarkup(runs: readonly Inline[]): string {
  return runs
    .map((run) => {
      if (run.href !== undefined) return `[${run.text}](${run.href})`;
      if (run.bold === true) return `**${run.text}**`;
      if (run.italic === true) return `*${run.text}*`;
      return run.text;
    })
    .join("");
}

const BULLET = /^[-*]\s+/;
const NUMBERED = /^\d+[.)]\s+/;
const CITE = /^(—|--)\s*/;

/** What staff typed into structured nodes. The result is checked against the rich text contract before saving. */
export function richTextFromMarkup(markup: string): Node[] {
  const nodes: Node[] = [];
  const chunks = markup
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0);
  for (const chunk of chunks) {
    const lines = chunk.split("\n").map((line) => line.trim());
    const first = lines[0] ?? "";
    if (first.startsWith("### ") || first.startsWith("## ")) {
      const level = first.startsWith("### ") ? 3 : 2;
      nodes.push({
        type: "heading",
        level,
        content: inlinesFromMarkup(lines.join(" ").replace(/^#{2,3}\s+/, "")),
      });
    } else if (lines.every((line) => BULLET.test(line))) {
      nodes.push({
        type: "list",
        ordered: false,
        items: lines.map((line) => inlinesFromMarkup(line.replace(BULLET, ""))),
      });
    } else if (lines.every((line) => NUMBERED.test(line))) {
      nodes.push({
        type: "list",
        ordered: true,
        items: lines.map((line) => inlinesFromMarkup(line.replace(NUMBERED, ""))),
      });
    } else if (first.startsWith(">")) {
      const last = lines[lines.length - 1] ?? "";
      const cited = lines.length > 1 && CITE.test(last);
      const quoted = (cited ? lines.slice(0, -1) : lines).map((line) => line.replace(/^>\s?/, ""));
      nodes.push({
        type: "quote",
        content: inlinesFromMarkup(quoted.join(" ")),
        ...(cited ? { cite: last.replace(CITE, "") } : {}),
      });
    } else {
      nodes.push({ type: "paragraph", content: inlinesFromMarkup(lines.join(" ")) });
    }
  }
  return nodes;
}

/** Stored nodes back into what the editor shows, so editing and saving without changes gives the same nodes. */
export function richTextToMarkup(nodes: readonly Node[]): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case "heading":
          return `${"#".repeat(node.level)} ${inlinesToMarkup(node.content)}`;
        case "list":
          return node.items
            .map(
              (item, index) =>
                `${node.ordered ? `${String(index + 1)}.` : "-"} ${inlinesToMarkup(item)}`,
            )
            .join("\n");
        case "quote":
          return `> ${inlinesToMarkup(node.content)}${node.cite === undefined ? "" : `\n— ${node.cite}`}`;
        case "paragraph":
          return inlinesToMarkup(node.content);
      }
    })
    .join("\n\n");
}
